import { Injectable } from '@angular/core';

import { newUuid } from '../pos-config.utils';

/**
 * PosConfigTilesService (Angular 18)
 *
 * Ported 1:1 from apps/b2b-ui/common/services/posConfig/posConfigTiles/posConfigTiles.service.js
 * (legacy name 'PosConfigTilesService'). Tile model management plus the
 * colour/hex/rgb(a) conversion helpers used by the POS tile editor.
 *
 * Internal sub-service of PosConfigService — injected directly, not
 * downgrade-registered. Legacy `uuid2.newuuid()` → native `newUuid()`
 * (see pos-config.utils.ts). Wave 3 #17.
 */
@Injectable({ providedIn: 'root' })
export class PosConfigTilesService {
  rgba = 'rgba(:r,:g,:b,:a)';

  model: any[] = [];

  height = 112;
  width = 112;

  padding = {
    top: 0,
    right: 8,
    bottom: 8,
    left: 0,
  };

  current: any;

  init(): boolean {
    return true;
  }

  emptyTile(x: any, y: any, value?: any): any {
    return {
      name: '',
      subTitle: '',
      x: x,
      y: y,
      type: '',
      showCapacity: undefined,
      color: this.rgbaToColor('rgba(255,255,255,1)'),
      isEmpty: true,
      config: {
        id: newUuid(),
        x: x,
        y: y,
        type: 'tile',
      },
    };
  }

  /**
   * Take color in tile and convert it to css string
   *
   * return {String} rgba(...)
   */
  colorToRgba(tile: any): string {
    const c = [
      tile.color.r || 0,
      tile.color.g || 0,
      tile.color.b || 0,
      tile.color.a !== null && tile.color.a !== 'undefined' ? tile.color.a : 1,
    ];

    return 'rgba(' + c.join(',') + ')';
  }

  /**
   * Take color in tile and convert it to css string
   *
   * return {String} rgb(...)
   */
  colorToRgb(tile: any): string {
    const c = [tile.color.r, tile.color.g, tile.color.b];

    return 'rgb(' + c.join(',') + ')';
  }

  /**
   * Convert rgba(...) string to color object
   */
  rgbaToColor(rgba: string): any {
    const c = rgba.substr(5).replace(')', '').split(',');

    return {
      r: c[0] ? parseInt(c[0], 10) : 0,
      g: c[1] ? parseInt(c[1], 10) : 0,
      b: c[2] ? parseInt(c[2], 10) : 0,
      a: c[3] ? parseFloat(c[3]) : 0,
    };
  }

  /**
   * Convert rgb(...) string to color object
   */
  rgbToColor(rgb: string): any {
    const c = rgb.substr(4).replace(')', '').split(',');

    return {
      r: parseInt(c[0], 10),
      g: parseInt(c[1], 10),
      b: parseInt(c[2], 10),
    };
  }

  /**
   * Convert hex string to color object
   */
  hexToColorRgb(hex: string): any {
    const c = hex.split('');

    if (c[0] === '#') {
      c.shift();
    }

    return {
      r: parseInt(c[0] + c[1], 16),
      g: parseInt(c[2] + c[3], 16),
      b: parseInt(c[4] + c[5], 16),
    };
  }

  /**
   * Convert hex string to color with alpha object
   */
  hexToColorRgba(hex: string): any {
    const c = hex.split('');

    if (c[0] === '#') {
      c.shift();
    }

    return {
      r: parseInt(c[0] + c[1], 16),
      g: parseInt(c[2] + c[3], 16),
      b: parseInt(c[4] + c[5], 16),
      a: parseFloat((parseInt(c[6] + c[7], 16) / 255).toPrecision(1)),
    };
  }

  /**
   * Convert color string to color with alpha object
   *
   * @param color hex or rgb(...)
   */
  toColor(color: string): any {
    let s;
    let r = this.hexToColorRgb('#000000');

    if (/^#[a-fA-F0-9]{8}$/.test(color)) {
      r = this.hexToColorRgba(color);
    } else if (/^#[a-fA-F0-9]{6}$/.test(color)) {
      r = this.hexToColorRgb(color);
    } else if (/^#[a-fA-F0-9]{4}$/.test(color)) {
      s = color.split('');
      r = this.hexToColorRgba('#' + [s[1], s[1], s[2], s[2], s[3], s[3], s[4], s[4]].join(''));
    } else if (/^#[a-fA-F0-9]{3}$/.test(color)) {
      s = color.split('');
      r = this.hexToColorRgb('#' + [s[1], s[1], s[2], s[2], s[3], s[3]].join(''));
    } else if (/^rgb\((\d{1,3},){2}\d{1,3}\)$/.test(color)) {
      r = this.rgbToColor(color);
    } else if (/^rgba\((\d{1,3},){3}[01]{1}(\.\d+)?\)$/.test(color)) {
      r = this.rgbaToColor(color);
    }

    return r;
  }

  /**
   * Convert rgb string to hex object
   *
   * @param color hex or rgb(...)
   */
  toHex(rgba: string): any {
    let r = this.hexToColorRgb('#000000');

    if (/^rgb\((\d{1,3},){2}\d{1,3}\)$/.test(rgba)) {
      console.log('rgb');
      r = this.rgbToHex(rgba);
    } else if (/^rgba\((\d{1,3},){3}[01]{1}(\.\d+)?\)$/.test(rgba)) {
      r = this.rgbaToHex(rgba);
    }

    return r;
  }

  /**
   * Convert rgb string to hex object
   */
  rgbToHex(rgb: string): string {
    let hex;
    const c = rgb.substr(4).replace(')', '').split(',');
    let r = this.hex(c[0]);
    let g = this.hex(c[1]);
    let b = this.hex(c[2]);

    r = r === '0' ? '00' : r;
    g = g === '0' ? '00' : g;
    b = b === '0' ? '00' : b;

    hex = '#' + [r, g, b].join('');

    return hex;
  }

  /**
   * Convert rgb string to hex object
   */
  rgbaToHex(rgba: string): string {
    let hex;
    const c = rgba.substr(5).replace(')', '').split(',');
    let r = this.hex(c[0]);
    let g = this.hex(c[1]);
    let b = this.hex(c[2]);
    let a = this.hex(parseFloat(c[3]) * 255);

    r = !r || r === '0' ? '00' : r;
    g = !g || g === '0' ? '00' : g;
    b = !b || b === '0' ? '00' : b;
    a = !a || a === '0' ? '00' : a;

    hex = '#' + [r, g, b, a].join('');

    return hex;
  }

  /**
   * Convert to hex from integer
   */
  hex(dec: any): string {
    let i = 0;
    let stack: any[] = [];
    let rem = 0;
    let res = dec;

    if (parseInt(dec, 10) < 10) {
      stack = [0, dec];
    } else if (parseInt(dec, 10) > 9 && parseInt(dec, 10) < 16) {
      stack = [0, String.fromCharCode(parseInt(dec, 10) + 55)];
    } else {
      while (res > 0) {
        i++;
        rem = res % 16;
        res = parseInt(String(res / 16), 10);
        stack.unshift(rem > 9 ? String.fromCharCode(rem + 55) : rem);
      }
    }

    return stack.join('');
  }

  getDims(): any {
    return {
      width: this.width + this.padding.left + this.padding.right,
      height: this.height + this.padding.top + this.padding.bottom,
    };
  }

  getPosition(x: any, y: any): any {
    const dims = this.getDims();

    return {
      x: x * dims.width,
      y: y * dims.height,
    };
  }

  getSlotPosition(x: any, y: any): any {
    const dims = this.getDims();

    return {
      x: x / dims.width,
      y: y / dims.height,
    };
  }

  getNextTileId(): number {
    let i, ii;
    let tmp = 1;

    for (i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].config.id > tmp) {
        tmp = this.model[i].config.id;
      }
    }

    return tmp + 1;
  }

  customColor(tile: any): string {
    return this.rgba
      .replace(/:r/, tile.color.r)
      .replace(/:g/, tile.color.g)
      .replace(/:b/, tile.color.b)
      .replace(/:a/, tile.color.a);
  }

  setModel(tiles: any): void {
    this.model = tiles instanceof Array ? tiles : [];
  }

  getModel(): any[] {
    return this.model;
  }

  getModelForPage(pageId: any): any[] {
    const tiles: any[] = [];
    let i, ii;

    for (i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].pageId === pageId) {
        tiles.push(this.model[i]);
      }
    }

    return tiles;
  }

  getType(tile: any, eventType: any): string {
    let r;

    if (Object.prototype.hasOwnProperty.call(tile, 'presell')) {
      r = 'presell';
    } else {
      if (Object.prototype.hasOwnProperty.call(tile, 'typeId')) {
        r = 'typed';
      } else {
        if (eventType === 'generalAdmission') {
          r = 'rollup';
        } else {
          r = 'typed';
        }
      }
    }

    return r;
  }

  setCurrentTile(id: any): void {
    this.current = this.findTile(id);
  }

  getCurrentTile(): any {
    let r: any = false;
    const found = this.findTile(this.current.tile.config.id);

    if (found) {
      r = this.model[found.index];
    }

    return r;
  }

  setTile(tile: any): void {
    const found = this.findTile(tile.config.id);

    if (found) {
      this.model[found.index] = tile;
    }
  }

  addTile(tile: any): void {
    this.model.push(tile);
  }

  removeTile(tile: any): void {
    const found = this.findTile(tile.config.id);
    let empty;

    if (found) {
      empty = this.emptyTile(found.tile.config.x, found.tile.config.y);
      empty.config.id = found.tile.config.id;

      this.model[found.index] = empty;
    }
  }

  findTile(id: any): any {
    let i, ii;
    let r: any = false;

    for (i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].config.id === id) {
        r = {
          tile: this.model[i],
          index: i,
        };
        break;
      }
    }

    return r;
  }

  process(tiles: any[]): any[] {
    let i, ii;
    const r: any[] = [];

    for (i = 0, ii = tiles.length; i < ii; i++) {
      if (tiles[i].config.type !== 'tile') {
        r.push(tiles[i]);
      }
    }

    return r;
  }
}
